import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Avatar, Box, CircularProgress, IconButton, Paper, TextField, Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { API_BASE_URL } from '../config';
import { captureAttribution } from '../utils/attribution';
import './ChatWidget.css';

/**
 * ChatWidget — embedded (inline) anonymous web chat for the /start LP.
 *
 * Mounts with Aven's opener, tracks exchange count up to `max_exchanges`
 * (defaults 3, authoritative value comes from the server), and calls
 * `onSignupNudge(sessionId)` once the cap is hit so the parent page can
 * scroll to the inline signup form.
 *
 * Props:
 *   - embedded (bool): currently always renders inline (floating mode
 *     reserved for future use).
 *   - onSignupNudge(sessionId): called when exchange cap is reached.
 *   - onSessionStart(sessionId): called when the session is created — the
 *     parent typically passes this to the signup form.
 */
// "Weight loss journey" (not "GLP-1 journey"): the title already targets
// GLP-1 users; the opener speaks to the want underneath. "What's the
// hardest part for you right now?" echoes the hero title and hands off
// directly to the pain-point chips below.
const OPENER = "Hi, I'm Aven \u{1F44B} A coach in your corner for your weight loss journey. What's the hardest part for you right now?";

// One-tap starters. Tapping sends the mapped message so the very first
// reply costs a tap, not a composed answer to a cold open — this is the
// main lever against the "loads but never types" drop-off on /start.
//
// Chips are the four research-backed biggest GLP-1 pain points (side
// effects, food, consistency, cost/access), written in the user's inner
// monologue. The opener's "What's the hardest part for you right now?"
// closes the loop with the hero title ("The hardest part of a GLP-1
// isn't the shot") — title, question, and chips read as one thought.
// No "what is Aven" chip: the page copy explains Aven; the chat's job
// is to demonstrate it by coaching on a real pain in reply #1.
const QUICK_REPLIES = [
  { label: 'The side effects are rough', message: 'The side effects have been rough on me.' },
  { label: "I don't know what to eat anymore", message: "I don't know what I should be eating anymore." },
  { label: 'I keep falling off track', message: "I keep falling off track and it's frustrating." },
  { label: 'Worried about cost and coverage', message: 'The cost stresses me out and I worry about keeping my medication covered.' },
];

const SESSION_KEY = 'aven_web_chat_session_id';
const SESSION_TOKEN_KEY = 'aven_web_chat_session_token';

// Module-level singleton promise for session bootstrapping. Survives
// component unmount/remount cycles — useRef only protects against
// double-fire on the *same* instance, but if the component remounts
// (parent key change, route transition, MUI Portal weirdness, etc.)
// the new instance has a fresh ref and would create a second server
// session. This shared promise means even if N instances mount, only
// ONE actual POST /web-chat/session goes out. Each instance awaits the
// same promise and reads back the same (session_id, session_token) pair.
//
// Why this matters: the signup form reads session_id+token from
// sessionStorage. If two boots both ran and both wrote sessionStorage,
// the form sees the *last* pair, but if the form's React state holds
// the *first* session_id (set via onSessionStart) we end up with a
// mismatch and the backend rejects the chat-transcript import.
let _sessionBootPromise = null;

/**
 * Rehydrate a session this tab already created. Read-only: it never creates
 * one, so calling it on mount costs nothing for a visitor who never engages.
 * Returns null when there is nothing to restore.
 */
async function restoreSession() {
  let existing = null;
  let existingToken = null;
  try {
    existing = sessionStorage.getItem(SESSION_KEY);
    existingToken = sessionStorage.getItem(SESSION_TOKEN_KEY);
  } catch (_) {}

  if (!existing) return null;

  try {
    const res = await axios.get(
      `${API_BASE_URL}/api/v1/web-chat/session/${existing}`,
      existingToken ? { headers: { 'X-Session-Token': existingToken } } : {}
    );
    const data = res.data || {};
    return {
      sessionId: existing,
      sessionToken: existingToken,
      maxExchanges: data.max_exchanges || 3,
      messages: data.messages || [],
      exchangeCount: data.exchange_count || 0,
    };
  } catch (_) {
    try { sessionStorage.removeItem(SESSION_KEY); } catch (_) {}
    try { sessionStorage.removeItem(SESSION_TOKEN_KEY); } catch (_) {}
    return null;
  }
}

/**
 * Get a usable session, creating one on the server if this tab has none.
 * Called from `send` — i.e. only once a human has actually said something.
 *
 * Routed through the module-level singleton promise so that N concurrent
 * callers (two widget instances, a double-tap that beats the ref guard) share
 * ONE POST and therefore one (session_id, session_token) pair. A mismatched
 * pair makes the backend reject the chat-transcript import at signup.
 */
function ensureSession() {
  if (!_sessionBootPromise) {
    _sessionBootPromise = (async () => {
      const restored = await restoreSession();
      if (restored) return restored;

      // Reads the first-touch record the mount effect already stored, so the
      // session carries the original fbclid/utm even though it is created
      // later in the visit.
      const attribution = captureAttribution();
      const res = await axios.post(
        `${API_BASE_URL}/api/v1/web-chat/session`,
        { attribution }
      );
      const sid = res.data.session_id;
      const tok = res.data.session_token;
      try { sessionStorage.setItem(SESSION_KEY, sid); } catch (_) {}
      try { sessionStorage.setItem(SESSION_TOKEN_KEY, tok); } catch (_) {}
      return {
        sessionId: sid,
        sessionToken: tok,
        maxExchanges: res.data.max_exchanges || 3,
        messages: [],
        exchangeCount: 0,
      };
    })().catch((err) => {
      // Clear so a later attempt can retry; re-throw so awaiters see it.
      _sessionBootPromise = null;
      throw err;
    });
  }
  return _sessionBootPromise;
}

export default function ChatWidget({ onSignupNudge, onSessionStart }) {
  const [sessionId, setSessionId] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: OPENER },
  ]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  // Synchronous in-flight guard. `sending` is React state and updates
  // async, so two rapid taps (or an iOS ghost click) can BOTH pass the
  // `sending` check before the re-render lands — double-sending the
  // message and burning an exchange. A ref flips synchronously.
  const sendingRef = useRef(false);
  const [exchangeCount, setExchangeCount] = useState(0);
  const [maxExchanges, setMaxExchanges] = useState(3);
  const [nudged, setNudged] = useState(false);
  const [error, setError] = useState(null);
  const bodyRef = useRef(null);

  // Push a session (restored or freshly created) into component state.
  const applySession = (result) => {
    if (!result) return;
    const { sessionId: sid, sessionToken: tok, maxExchanges: max, messages: msgs, exchangeCount: ec } = result;
    setSessionId(sid);
    setSessionToken(tok);
    setMaxExchanges(max || 3);
    if (typeof ec === 'number') setExchangeCount(ec);
    if (Array.isArray(msgs) && msgs.length) {
      setMessages([{ role: 'assistant', content: OPENER }, ...msgs]);
    }
    if (onSessionStart) onSessionStart(sid);
  };
  // On mount we RESTORE a session this tab already has, but we do not create
  // one. Session creation moved to first user intent (see `send`) — before
  // that, mounting the widget POSTed /web-chat/session, so every visitor got
  // a server session and an LLM-backed conversation record whether or not
  // they ever typed. That was tolerable only while robots.txt blocked all
  // crawling; now that the site is indexable, every crawler executing our JS
  // would burn a session. It also makes the funnel honest: lp_view and
  // chat_session_start tracked ~1:1 before this, so the pair said nothing
  // about engagement. Now chat_session_start means a human started talking.
  useEffect(() => {
    let cancelled = false;

    // Capture attribution at MOUNT even though the session is created later.
    // captureAttribution() is first-touch and stores to sessionStorage, and it
    // snapshots window.location.href / document.referrer — which are only
    // guaranteed correct on the landing hit. Deferring the capture itself to
    // send-time would risk recording a post-navigation URL and losing fbclid.
    // This call is local-only: no network, safe for crawlers.
    captureAttribution();

    restoreSession()
      .then((result) => {
        if (cancelled || !result) return;
        applySession(result);
      })
      .catch(() => { /* a failed restore just means "no session yet" */ });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-scroll to newest message — scroll only inside the chat body so the
  // page itself doesn't move when a new message is appended.
  useEffect(() => {
    const body = bodyRef.current;
    if (body) {
      body.scrollTop = body.scrollHeight;
    }
  }, [messages, sending]);

  const atLimit = exchangeCount >= maxExchanges;
  // Show one-tap starters only before the user has said anything.
  const showQuickReplies =
    !atLimit && exchangeCount === 0 && !messages.some((m) => m.role === 'user');

  const send = async (explicitText) => {
    const text = (explicitText !== undefined ? explicitText : input).trim();
    // No `!sessionId` guard any more: the first send is what creates the
    // session. `atLimit` still gates, and maxExchanges defaults to 3 until a
    // session says otherwise, so a pre-session send can never exceed the cap.
    if (!text || sending || sendingRef.current || atLimit) return;
    sendingRef.current = true;

    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    if (explicitText === undefined) setInput('');
    setSending(true);

    try {
      // Create the session now if this is the first thing the visitor said.
      let sid = sessionId;
      let tok = sessionToken;
      if (!sid) {
        const session = await ensureSession();
        sid = session.sessionId;
        tok = session.sessionToken;
        applySession(session);
      }

      const res = await axios.post(
        `${API_BASE_URL}/api/v1/web-chat/message`,
        { session_id: sid, message: text },
        tok ? { headers: { 'X-Session-Token': tok } } : {}
      );
      const { response, exchange_count, nudge_signup } = res.data || {};
      setMessages((prev) => [...prev, { role: 'assistant', content: response || '...' }]);
      setExchangeCount(exchange_count || 0);
      if (nudge_signup && !nudged) {
        setNudged(true);
        if (onSignupNudge) onSignupNudge(sid);
      }
    } catch (err) {
      if (!sessionId && !_sessionBootPromise) {
        // The session itself could not be created, so there is no conversation
        // to attach an error bubble to. Surface it in the banner instead and
        // drop the optimistic user message — nothing was sent.
        setMessages((prev) => prev.filter((m) => m.content !== text || m.role !== 'user'));
        setError(
          err?.response?.status === 429
            ? "We're a little busy right now. Try again in a bit."
            : 'Having trouble starting the chat — refresh to try again.'
        );
      } else {
        const detail = err?.response?.data?.detail || 'Something went wrong — try again.';
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: detail, isError: true },
        ]);
      }
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <Paper elevation={0} className="aven-chat-widget" role="region" aria-label="Chat with Aven">
      <Box className="aven-chat-header">
        <Avatar sx={{ bgcolor: '#2e7d32', width: 32, height: 32, mr: 1.5 }}>A</Avatar>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Aven <Typography component="span" variant="caption" color="text.secondary">· by Beck Health</Typography>
          </Typography>
          {/* Objection-killer at the point of action. Deliberately just
              "Try it free" — the web chat IS free with no signup, but the
              product is $20/mo later, so anything stronger over-promises.
              The mid-page section discloses pricing transparently. */}
          <Typography variant="caption" color="text.secondary">
            Try it free
          </Typography>
        </Box>
      </Box>

      <Box ref={bodyRef} className="aven-chat-body" aria-live="polite">
        {messages.map((m, i) => (
          <Box
            key={i}
            className={`aven-chat-bubble ${m.role === 'user' ? 'is-user' : 'is-aven'} ${m.isError ? 'is-error' : ''}`}
          >
            {m.content}
          </Box>
        ))}
        {sending && (
          <Box className="aven-chat-bubble is-aven is-typing">
            <span className="dot" /><span className="dot" /><span className="dot" />
          </Box>
        )}
        {showQuickReplies && !sending && (
          <Box className="aven-chat-quick-replies">
            {QUICK_REPLIES.map((qr) => (
              <button
                key={qr.label}
                type="button"
                className="aven-chat-chip"
                disabled={sending}
                onClick={() => send(qr.message)}
              >
                {qr.label}
              </button>
            ))}
          </Box>
        )}
        {atLimit && (
          <Box className="aven-chat-cap-notice">
            Let's keep going on your phone — texting is where I do my best
            coaching. Drop your number below and I'll pick up right here.
          </Box>
        )}
      </Box>

      <Box className="aven-chat-input-row">
        <TextField
          fullWidth
          size="small"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={atLimit ? 'Sign up below to keep chatting' : 'Type your answer…'}
          disabled={sending || atLimit}
          inputProps={{ 'aria-label': 'Message Aven' }}
        />
        <IconButton
          onClick={() => send()}
          disabled={sending || atLimit || !input.trim()}
          color="primary"
          aria-label="Send message"
        >
          {sending ? <CircularProgress size={20} /> : <SendIcon />}
        </IconButton>
      </Box>

      {error && (
        <Typography variant="caption" sx={{ px: 2, pb: 1.5, color: '#86868b' }}>
          {error}
        </Typography>
      )}
    </Paper>
  );
}
